import * as React from 'react';
import { Tabs, Tab } from 'material-ui/Tabs';
import TextField from 'material-ui/TextField';
import RaisedButton from 'material-ui/RaisedButton';
import FlatButton from 'material-ui/FlatButton';
import SelectField from 'material-ui/SelectField';
import MenuItem from 'material-ui/MenuItem';
import DatePicker from 'material-ui/DatePicker';
import Dialog from 'material-ui/Dialog';
import IconButton from 'material-ui/IconButton';
import Checkbox from 'material-ui/Checkbox';
import FontIcon from 'material-ui/FontIcon';
import { black } from 'material-ui/styles/colors';
import Phone from 'react-phone-number-input';
import 'react-accessible-accordion/dist/fancy-example.css';
import {
  Member,
  MembersDetailsMode,
  GenderCode,
  PaymentTermCode,
  MaxAttendanceCode,
  PaymentInfo,
  MemberType,
  MembersState,
  MakePaymentRequest
} from './members.types';
import {
  genders,
  DefaultMemberPictureURL,
  maxAttendances,
  DEFAULT_MEMBER_ID,
  EDIT,
  DISPLAY,
  TIME_BASED,
  CLASS_BASED,
  ACTIVATE_MEMBERSHIP_BUTTON_CLICKED,
  MAX_NOTE_LENGTH,
  PACKAGE_BASED,
  DEFAULT_PAYMENT,
  YEAR_3000_PACKAGE_BASED_END_DATE
} from './members.constants';
import ImageUploadComponent from '../util/ImageUploadComponent';
import TabPanel from '../commons/TabPanel';
import ProgramMember from './programMember';
import PosHistories from './PosHistories';
import TestHistories from './testHistories';
import AddressOnlyComponent from '../geo/AddressOnlyComponent';
import { AddressOnlyInfo, AddressOnlyInfoError } from '../geo/address.types';
import {
  isValidName,
  isValidEmail,
  isValidPhone,
  compareNumber,
  generateEnterValidErrorMessage
} from '../util/Validation';
import {
  calculateAge,
  getLocalDate
} from '../util/date-util';
import { isPaymentDisabled, cannotWrite, cannotUpdate } from '../util/permission.util';
import { AuthState } from '../auth/auth.types';
import { AdminState } from '../admin/admin.types';
import { MEMBER } from '../account/Constants';
import { BusinessState } from '../business/business.types';
import { RadioButton, RadioButtonGroup } from 'material-ui/RadioButton';
import Chip from 'material-ui/Chip';
import * as validator from 'validator';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './reactDatepickerCustomStyle.css';
import { REMAINED, UPDATED } from '../programs/Constants';
import PaymentTable from './PaymentTable';
import { mmsStore } from 'src/app/app.config';
import { MmsColor } from 'src/app/app.const';
import './MembersDetails.css';

const MAX_LENGTH_FOR_DB = 20;

const PAYMENT_START_DAYS = [1, 5, 10, 15, 20, 25];

type PaymentStartDate = {
  readonly year: number
  readonly month: number
  readonly day: number
};

const fieldStyles = {
  rootStyle: {
    cursor: 'text'
  },
  textStyle: {
    color: black
  },
  dividerStyle: {
    backgroundColor: 'rgb(0, 188, 212)'
  }
};

type Props = {
  readonly authState: AuthState
  readonly adminState: AdminState
  readonly membersState: MembersState
  readonly member: Member
  readonly mode: MembersDetailsMode
  readonly onMemberImageFileChangeHandler?: (fileFormatError: string, fileSizeError: string, file: File) => void
  readonly memberChangeHandler?: (updatedMember: Member, hasError: boolean) => void
  readonly businessState: BusinessState
  readonly selectedMemberType?: MemberType
  readonly selectedMemberTypeChangeHandler?: (updated: MemberType) => void
  readonly isContinuousPayment?: boolean
  readonly selectedCancelPayment?: boolean
  readonly handleCancelPaymentClick?: () => void
  readonly selectedSendCancellationNotification?: boolean
  readonly handleSendCancellationNotificationClick?: () => void
  readonly fileSizeError: string
  readonly fileFormatError: string
  readonly makePaymentRequestHandler: (r: MakePaymentRequest) => void
  readonly makePaymentRequest?: MakePaymentRequest
  readonly handleRedirectToPaymentListPage?: Function
};

type MembersDetailsError = {
  readonly amountPerTransactionError: string
  readonly firstNameError: string
  readonly lastNameError: string
  readonly initialError: string
  readonly startEndDateError: string
  readonly emailInfoError: string
  readonly downPaymentError: string
  readonly noteError: string
  readonly emergencyContactName1Error: string
  readonly emergencyContactNumber1Error: string
  readonly emergencyContactName2Error: string
  readonly emergencyContactNumber2Error: string
  readonly emergencyContactName3Error: string
  readonly emergencyContactNumber3Error: string
};

type PaymentError = {
  readonly numOfTransactionError: string
};

type States = {
  readonly originalMember: Member
  readonly member: Member
  readonly pointToUpdate: number
  readonly testDetailsDialogOpen: boolean
  readonly warningDialogOpen: boolean
  readonly warningDialogMessage: string
  readonly warningDialogCallback?: Function
  readonly paymentInformationExpanded: boolean
  readonly membersDetailsError: MembersDetailsError
  readonly isItEditMode: boolean
  readonly paymentStartDate: PaymentStartDate
  readonly classesToAdd: number
  readonly selectedTags: string[]
  readonly tag: string
  readonly isContinuousPayment: boolean
  readonly activateMembershipConfirm: boolean
  readonly paymentError: PaymentError
  readonly fileSizeError: string
  readonly fileFormatError: string
  readonly openFileErrorModal: boolean
  readonly addressOnlyInfo: AddressOnlyInfo
  readonly addressOnlyInfoError: AddressOnlyInfoError
  readonly mobile1Error: string
  readonly mobile2Error: string
};

class MembersDetailsComponent extends React.Component<Props, States> {
  constructor(props: Props) {
    super(props);

    this.state = {
      originalMember: props.member,
      member: props.member,
      testDetailsDialogOpen: false,
      warningDialogOpen: false,
      warningDialogMessage: '',
      warningDialogCallback: undefined,
      paymentInformationExpanded: props.member.id === DEFAULT_MEMBER_ID,
      membersDetailsError: {
        amountPerTransactionError: '',
        firstNameError: '',
        lastNameError: '',
        initialError: '',
        startEndDateError: '',
        emailInfoError: '',
        downPaymentError: '',
        noteError: '',
        emergencyContactName1Error: '',
        emergencyContactNumber1Error: '',
        emergencyContactName2Error: '',
        emergencyContactNumber2Error: '',
        emergencyContactName3Error: '',
        emergencyContactNumber3Error: ''
      },
      isItEditMode: props.mode === EDIT,
      paymentStartDate: {
        year: new Date(this.getFirstPaymentFromMember(props).startDate).getFullYear(),
        month: new Date(this.getFirstPaymentFromMember(props).startDate).getMonth() + 1,
        day: PAYMENT_START_DAYS.includes(new Date(this.getFirstPaymentFromMember(props).startDate).getDate()) ?
          new Date(this.getFirstPaymentFromMember(props).startDate).getDate() :
          1
      },
      classesToAdd: 0,
      pointToUpdate: 0,
      selectedTags: props.member.tags,
      tag: '',
      isContinuousPayment: this.getFirstPaymentFromMember(props).continuous,
      activateMembershipConfirm: false,
      paymentError: { numOfTransactionError: '' },
      fileSizeError: props.fileSizeError,
      fileFormatError: props.fileFormatError,
      openFileErrorModal: props.fileSizeError !== '' || props.fileFormatError !== '',
      addressOnlyInfo: {
        address: props.member.address,
        city: props.member.city,
        country: props.member.country,
        province: props.member.province,
        postalCode: props.member.postalCode
      },
      addressOnlyInfoError: {
        addressError: '',
        cityError: '',
        countryError: '',
        provinceError: '',
        postalCodeError: ''
      },
      mobile1Error: '',
      mobile2Error: ''
    };

    this.toggleWarningDialog = this.toggleWarningDialog.bind(this);
    this.toggleMemershipStatus = this.toggleMemershipStatus.bind(this);
    this.onFirstNameChange = this.onFirstNameChange.bind(this);
    this.onLastNameChange = this.onLastNameChange.bind(this);
    this.onInitialChange = this.onInitialChange.bind(this);
    this.onGenderChange = this.onGenderChange.bind(this);
    this.onBirthDateChange = this.onBirthDateChange.bind(this);
    this.onMaxAttendancePerWeekChange = this.onMaxAttendancePerWeekChange.bind(this);
    this.onPointToUpdateChange = this.onPointToUpdateChange.bind(this);
    this.onEmailChange = this.onEmailChange.bind(this);
    this.onNoteChange = this.onNoteChange.bind(this);
    this.onMembershipStartDateChange = this.onMembershipStartDateChange.bind(this);
    this.onMembershipEndDateChange = this.onMembershipEndDateChange.bind(this);
    this.onContractDurationChange = this.onContractDurationChange.bind(this);
    this.onSmsConsentChange = this.onSmsConsentChange.bind(this);
    this.onEmailConsentChange = this.onEmailConsentChange.bind(this);
    this.onProgramMembersChangeHandler = this.onProgramMembersChangeHandler.bind(this);
    this.hasMemberDetailInfoError = this.hasMemberDetailInfoError.bind(this);
    this.renderCancelButton = this.renderCancelButton.bind(this);
    this.renderSubmitButton = this.renderSubmitButton.bind(this);
    this.getFirstPaymentFromMember = this.getFirstPaymentFromMember.bind(this);
  }

  componentWillReceiveProps(nextProps: Props) {
    if (this.props !== nextProps) {
      this.setState({
        member: nextProps.member,
        addressOnlyInfo: {
          address: nextProps.member.address,
          city: nextProps.member.city,
          country: nextProps.member.country,
          province: nextProps.member.province,
          postalCode: nextProps.member.postalCode
        },
        fileSizeError: nextProps.fileSizeError,
        fileFormatError: nextProps.fileFormatError,
        openFileErrorModal: nextProps.fileSizeError !== '' || nextProps.fileFormatError !== ''
      });
    }
  }

  render() {
    return (
      <div className="bg-white">
        {this.renderMemberDetailsMainSection()}
        <Dialog
          title="Warning"
          actions={[
            this.renderCancelButton(() => this.toggleWarningDialog('')),
            this.renderSubmitButton(() =>
              this.state.warningDialogCallback ? this.state.warningDialogCallback() : this.toggleWarningDialog(''))
          ]}
          modal={true}
          open={this.state.warningDialogOpen}
          onRequestClose={() => this.toggleWarningDialog('')}
        >
          <div className="row mb-4">
            <div className="col-auto">
              {this.state.warningDialogMessage}
            </div>
          </div>
          <div className="row">
            <div className="col-5">
              <Checkbox
                checked={this.props.selectedCancelPayment ? this.props.selectedCancelPayment : false}
                onCheck={this.props.handleCancelPaymentClick ? this.props.handleCancelPaymentClick : null}
                label="Also want to cancel all payments"
              />
            </div>
          </div>
          <div className="row">
            <div className="col-5">
              <Checkbox
                checked={this.props.selectedSendCancellationNotification ?
                  this.props.selectedSendCancellationNotification :
                  false
                }
                onCheck={this.props.handleSendCancellationNotificationClick ?
                  this.props.handleSendCancellationNotificationClick :
                  null
                }
                label="Send a notification to the member"
              />
            </div>
          </div>
        </Dialog>
        {this.renderFileErrorModal()}
      </div>
    );
  }

  private renderMemberDetailsMainSection = (): JSX.Element => {
    const {
      mode,
      adminState,
      authState
    } = this.props;

    const {
      member
    } = this.state;

    const isDisplayMode = mode.toUpperCase().indexOf(DISPLAY) !== -1;

    return (
      <div>
        <div className="row">
          <div className="col-lg-4 col-md-4 col-sm-12 col-xs-12 pt-5">
            <div className="row">
              <div className="col-12 text-center">
                {this.renderMemberProfilePicture(isDisplayMode)}
              </div>
            </div>
            <div className="row">
              <div className="col-12 text-center">
                <div
                  style={{ fontSize: '23pt' }}
                >
                  <strong>
                    {member.firstName} {member.lastName}
                    {
                      (member.initial) ? ' (' + member.initial + ')' : null
                    }
                  </strong>
                </div>
              </div>
              <div className="col-12 text-center">
                <Phone
                  className="profilePhoneNumber"
                  countries={['CA']}
                  value={member.mobile1}
                  convertToNational={true}
                  inputStyle={{
                    backgroundColor: 'inherit',
                    borderBottom: '0px',
                    height: '40px',
                    fontSize: '18pt',
                    color: MmsColor.MmsBlue,
                    width: (member.mobile1) ? member.mobile1.length + 'ch' : '0ch'
                  }}
                  disabled={true}
                  onChange={Function()}
                />
              </div>
              {
                (member.id === DEFAULT_MEMBER_ID) ? (
                  null
                ) : (
                    <div className="col-12 text-center pt-4 pb-4">
                      <RaisedButton
                        label={(member.cancelled ? 'Activate' : 'Cancel') + ' Membership'}
                        secondary={true}
                        disabled={
                          cannotUpdate(MEMBER, authState, adminState)
                        }
                        onClick={() => {
                          if (member.cancelled) {
                            this.handleActivateMembership();
                          } else {
                            this.toggleWarningDialog(
                              'This member\'s membership will be cancelled when you update this member.',
                              this.toggleMemershipStatus
                            );
                          }
                        }}
                      />
                    </div>
                  )
              }
            </div>
          </div>
          <div
            className="col-lg-8 col-md-8 col-sm-12 col-xs-12 p-4"
            style={{ backgroundColor: MmsColor.LightGrey }}
          >
            {
              (member.id === DEFAULT_MEMBER_ID) ? (
                this.renderMemberInformationTab()
              ) : (
                  this.renderMemberDetailTabs()
                )
            }
          </div>
        </div>
      </div>
    );
  }

  private renderMemberDetailTabs = (): JSX.Element => {
    return (
      <Tabs
        tabItemContainerStyle={{
          backgroundColor: MmsColor.LightGrey,
        }}
        inkBarStyle={{ backgroundColor: 'red', height: '5pt' }}
      >
        <Tab
          label="Member Info"
          className="mb-3"
          buttonStyle={{ fontSize: '20pt', fontFamily: 'Roboto' }}
        >
          {this.renderMemberInformationTab()}
        </Tab>
        <Tab
          label="Payment Info"
          className="mb-3"
          buttonStyle={{ fontSize: '20pt', fontFamily: 'Roboto' }}
        >
          {this.renderPaymentInformationTab()}
        </Tab>
        <Tab
          label="Program Info"
          className="mb-3"
          buttonStyle={{ fontSize: '20pt', fontFamily: 'Roboto' }}
        >
          {this.renderProgramInformationTab()}
        </Tab>
      </Tabs>
    );
  }

  private renderFileErrorModal = (): JSX.Element => {
    return (
      <Dialog
        title="Image File Error"
        actions={[
          this.renderFileErrorOkayButton()
        ]}
        modal={true}
        open={this.state.openFileErrorModal}
        onRequestClose={() =>
          this.setState(
            { openFileErrorModal: false }
          )}
      >
        {
          this.state.fileSizeError ? this.state.fileSizeError : this.state.fileFormatError
        }
      </Dialog>
    );
  }

  private renderFileErrorOkayButton = (): JSX.Element => {
    return (
      <FlatButton
        label="Okay"
        primary={true}
        keyboardFocused={true}
        onClick={() =>
          this.setState(
            { openFileErrorModal: false },
            () => {
              if (this.props.onMemberImageFileChangeHandler) {
                this.props.onMemberImageFileChangeHandler('', '', undefined);
              }
            }
          )}
      />
    );
  }

  private renderMemberInformationTab = (): JSX.Element => {

    return (
      <TabPanel
        className="mt-4"
        tabs={[
          { title: 'Member Information', content: this.renderMemberInformationPanel() },
          { title: 'Membership Information', content: this.renderMembershipInformationPanel() },
          { title: 'Emergency Contact', content: this.renderMemberEmergencyContactPanel() }
        ]}
      />
    );
  }

  private renderMemberInformationPanel = (): JSX.Element => {
    const {
      mode
    } = this.props;

    const isDisplayMode = mode.toUpperCase().indexOf(DISPLAY) !== -1;

    return (
      <div className="row">
        <div className="col-12">
          {this.renderMemberInfoFirstRow(isDisplayMode)}
          {this.renderMemberInfoSecondRow(isDisplayMode)}
          {this.renderMemberInfoThirdRow(isDisplayMode)}
          {this.renderMemberInfoCheckInNotificationRow(isDisplayMode)}
        </div>
      </div>
    );
  }

  private renderMembershipInformationPanel = (): JSX.Element => {
    const {
      mode
    } = this.props;

    const isDisplayMode = mode.toUpperCase().indexOf(DISPLAY) !== -1;

    return (
      <div className="row">
        <div className="col-12">
          {this.renderMembershipInfoSection(isDisplayMode)}
        </div>
      </div>
    );
  }

  private renderMemberEmergencyContactPanel = (): JSX.Element => {
    const { mode, adminState, authState } = this.props;
    const { membersDetailsError, member } = this.state;

    const isDisplayMode = mode.toUpperCase().indexOf(DISPLAY) !== -1;

    return (
      <div className="row">
        <div className="col-12 mb-4">
          <div className="row">
            <div className="col-12">
              <TextField
                fullWidth={true}
                floatingLabelText="Contact Name 1"
                floatingLabelFixed={true}
                errorText={membersDetailsError.emergencyContactName1Error}
                value={member.emergencyContactName1}
                onChange={this.onEmergencyContactName1Change}
                disabled={
                  isDisplayMode ||
                  (
                    cannotWrite(MEMBER, authState, adminState) &&
                    cannotUpdate(MEMBER, authState, adminState)
                  )
                }
                style={(isDisplayMode) ? fieldStyles.rootStyle : {}}
                inputStyle={(isDisplayMode) ? fieldStyles.textStyle : {}}
                floatingLabelStyle={(isDisplayMode) ? fieldStyles.textStyle : {}}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <TextField
                fullWidth={true}
                floatingLabelText="Contact Phone Number 1"
                floatingLabelFixed={true}
                errorText={membersDetailsError.emergencyContactNumber1Error}
                disabled={
                  isDisplayMode ||
                  (
                    cannotWrite(MEMBER, authState, adminState) &&
                    cannotUpdate(MEMBER, authState, adminState)
                  )
                }
                style={(isDisplayMode) ? fieldStyles.rootStyle : {}}
                inputStyle={(isDisplayMode) ? fieldStyles.textStyle : {}}
                floatingLabelStyle={(isDisplayMode) ? fieldStyles.textStyle : {}}
              >
                <Phone
                  countries={['CA']}
                  value={member.emergencyContactNumber1}
                  onChange={this.onEmergencyContactNumber1Change}
                  convertToNational={true}
                  inputStyle={{ backgroundColor: 'inherit', borderBottom: '0px' }}
                />
              </TextField>
            </div>
          </div>
        </div>
        <div className="col-12 mb-4">
          <div className="row">
            <div className="col-12">
              <TextField
                fullWidth={true}
                floatingLabelText="Contact Name 2"
                floatingLabelFixed={true}
                errorText={membersDetailsError.emergencyContactName2Error}
                value={member.emergencyContactName2}
                onChange={this.onEmergencyContactName2Change}
                disabled={
                  isDisplayMode ||
                  (
                    cannotWrite(MEMBER, authState, adminState) &&
                    cannotUpdate(MEMBER, authState, adminState)
                  )
                }
                style={(isDisplayMode) ? fieldStyles.rootStyle : {}}
                inputStyle={(isDisplayMode) ? fieldStyles.textStyle : {}}
                floatingLabelStyle={(isDisplayMode) ? fieldStyles.textStyle : {}}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <TextField
                fullWidth={true}
                floatingLabelText="Contact Phone Number 2"
                floatingLabelFixed={true}
                errorText={membersDetailsError.emergencyContactNumber2Error}
                disabled={
                  isDisplayMode ||
                  (
                    cannotWrite(MEMBER, authState, adminState) &&
                    cannotUpdate(MEMBER, authState, adminState)
                  )
                }
                style={(isDisplayMode) ? fieldStyles.rootStyle : {}}
                inputStyle={(isDisplayMode) ? fieldStyles.textStyle : {}}
                floatingLabelStyle={(isDisplayMode) ? fieldStyles.textStyle : {}}
              >
                <Phone
                  countries={['CA']}
                  value={member.emergencyContactNumber2}
                  onChange={this.onEmergencyContactNumber2Change}
                  convertToNational={true}
                  inputStyle={{ backgroundColor: 'inherit', borderBottom: '0px' }}
                />
              </TextField>
            </div>
          </div>
        </div>
        <div className="col-12">
          <div className="row">
            <div className="col-12">
              <TextField
                fullWidth={true}
                floatingLabelText="Contact Name 3"
                floatingLabelFixed={true}
                errorText={membersDetailsError.emergencyContactName3Error}
                value={member.emergencyContactName3}
                onChange={this.onEmergencyContactName3Change}
                disabled={
                  isDisplayMode ||
                  (
                    cannotWrite(MEMBER, authState, adminState) &&
                    cannotUpdate(MEMBER, authState, adminState)
                  )
                }
                style={(isDisplayMode) ? fieldStyles.rootStyle : {}}
                inputStyle={(isDisplayMode) ? fieldStyles.textStyle : {}}
                floatingLabelStyle={(isDisplayMode) ? fieldStyles.textStyle : {}}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <TextField
                fullWidth={true}
                floatingLabelText="Contact Phone Number 3"
                floatingLabelFixed={true}
                errorText={membersDetailsError.emergencyContactNumber3Error}
                disabled={
                  isDisplayMode ||
                  (
                    cannotWrite(MEMBER, authState, adminState) &&
                    cannotUpdate(MEMBER, authState, adminState)
                  )
                }
                style={(isDisplayMode) ? fieldStyles.rootStyle : {}}
                inputStyle={(isDisplayMode) ? fieldStyles.textStyle : {}}
                floatingLabelStyle={(isDisplayMode) ? fieldStyles.textStyle : {}}
              >
                <Phone
                  countries={['CA']}
                  value={member.emergencyContactNumber3}
                  onChange={this.onEmergencyContactNumber3Change}
                  convertToNational={true}
                  inputStyle={{ backgroundColor: 'inherit', borderBottom: '0px' }}
                />
              </TextField>
            </div>
          </div>
        </div>
      </div>
    );
  }

  private renderPaymentInformationTab = (): JSX.Element => {
    const { adminState, authState } = this.props;

    const {
      member,
      isItEditMode,
      isContinuousPayment
    } = this.state;

    return (
      (isPaymentDisabled(authState, adminState)) ? (
        <TabPanel
          className="mt-4"
          tabs={[
            {
              title: 'Payment Information',
              content: <div className="row">
                <div className="col-12 p-5">
                  Payment is Disabled
                </div>
              </div>
            }
          ]}
        />
      ) : (
          <TabPanel
            className="mt-4"
            tabs={[
              {
                title: 'Payments',
                content: <PaymentTable
                  amountPerTransactionError={this.state.membersDetailsError.amountPerTransactionError}
                  isItEditMode={isItEditMode}
                  isContinuousPayment={isContinuousPayment}
                  renderAmountEditable={this.renderAmountEditable}
                  renderChargeDateEditable={this.renderChargeDateEditable}
                  makePaymentRequestHandler={this.props.makePaymentRequestHandler}
                  makePaymentRequest={this.props.makePaymentRequest}
                  handleRedirectToPaymentListPage={this.props.handleRedirectToPaymentListPage}
                />
              },
              {
                title: 'Purchase History',
                content: (member.id === DEFAULT_MEMBER_ID || this.props.membersState.selectedPayment) ?
                  null :
                  <div className="row">
                    <div className="col-12">
                      <PosHistories
                        posHistories={member.posHistories}
                      />
                    </div>
                  </div>
              }
            ]}
          />
        )
    );
  }

  private renderProgramInformationTab = (): JSX.Element => {
    const {
      member
    } = this.state;

    return (
      <TabPanel
        className="mt-4"
        tabs={[
          {
            title: 'Programs',
            content: <ProgramMember
              programMembers={member.programMembers}
              onProgramMembersChangeHandler={this.onProgramMembersChangeHandler}
              maxAttendancePerWeek={member.maxAttendancePerWeek}
            />
          },
          {
            title: 'Test Histories',
            content: <TestHistories
              testHistories={member.testHistories}
              programMembers={member.programMembers}
            />
          }
        ]}
      />
    );
  }

  private handleActivateMembership = (): void => {
    if (this.props.handleCancelPaymentClick) {
      this.setState(
        {
          warningDialogOpen: false,
          isItEditMode: false,
          isContinuousPayment: false
        },
        () => {
          this.setState(
            { activateMembershipConfirm: true },
            () => {
              if (this.props.memberChangeHandler) {
                this.props.memberChangeHandler(
                  {
                    ...this.state.member,
                    cancelled: !this.props.member.cancelled
                  },
                  this.hasMemberDetailInfoError()
                );
              }
            }
          );
        }
      );
    }
  }

  private renderActivateMembershipConfirmDialog = (): JSX.Element => {
    return (
      <Dialog
        title="Confirm Activate Membership"
        actions={[
          this.renderConfirmActivateMembershipConfirm()
        ]}
        modal={true}
        open={this.state.activateMembershipConfirm}
        onRequestClose={() => this.setState({ activateMembershipConfirm: false })}
      >
        Payment section has been reset. Please fill the payment section.
      </Dialog>
    );
  }

  private renderConfirmActivateMembershipConfirm = (): JSX.Element => {
    return (
      <FlatButton
        label="Okay"
        primary={true}
        keyboardFocused={true}
        onClick={() => this.setState({ activateMembershipConfirm: false })}
      />
    );
  }

  private renderMemberProfilePicture = (isDisplayMode: boolean): JSX.Element => {
    const { onMemberImageFileChangeHandler } = this.props;
    const { member } = this.state;

    return (
      <ImageUploadComponent
        defaultImageUrl={member.picUrl ? member.picUrl : DefaultMemberPictureURL[member.gender]}
        onImageFileChangeHandler={onMemberImageFileChangeHandler ? onMemberImageFileChangeHandler : undefined}
        mode={(isDisplayMode) ? DISPLAY : ''}
        isAvatar={true}
      />
    );
  }

  private renderMembershipInfoSection = (isDisplayMode: boolean): JSX.Element => {
    const { adminState, authState } = this.props;
    const { membersDetailsError, member } = this.state;

    return (
      <div>
        <div className="row">
          <div className="col-md-4 col-sm-12">
            <div className="input-group mb-2 mr-sm-2 mb-sm-0">
              <DatePicker
                fullWidth={true}
                className="material-input-required-red w-100"
                floatingLabelText="Start Date"
                value={member.startDate ? getLocalDate(new Date(member.startDate)) : getLocalDate(new Date())}
                onChange={this.onMembershipStartDateChange}
                disabled={
                  isDisplayMode ||
                  (cannotWrite(MEMBER, authState, adminState) && cannotUpdate(MEMBER, authState, adminState))
                }
                style={(isDisplayMode) ? fieldStyles.rootStyle : {}}
                inputStyle={(isDisplayMode) ? fieldStyles.textStyle : {}}
                floatingLabelStyle={(isDisplayMode) ? fieldStyles.textStyle : {}}
              />
            </div>
          </div>
          {this.props.selectedMemberType !== PACKAGE_BASED ?
            <div className="col-md-4 col-sm-12">
              <div className="input-group mb-2 mr-sm-2 mb-sm-0">
                <DatePicker
                  fullWidth={true}
                  className="material-input-required-red w-100"
                  floatingLabelText="End Date"
                  errorText={membersDetailsError.startEndDateError}
                  value={member.endDate ? getLocalDate(new Date(member.endDate)) : getLocalDate(new Date())}
                  onChange={this.onMembershipEndDateChange}
                  disabled={
                    isDisplayMode ||
                    (cannotWrite(MEMBER, authState, adminState) && cannotUpdate(MEMBER, authState, adminState))
                  }
                  style={(isDisplayMode) ? fieldStyles.rootStyle : {}}
                  inputStyle={(isDisplayMode) ? fieldStyles.textStyle : {}}
                  floatingLabelStyle={(isDisplayMode) ? fieldStyles.textStyle : {}}
                />
              </div>
            </div> :
            null
          }
          <div className="col-md-4 col-sm-12">
            <div className="input-group mb-2 mr-sm-2 mb-sm-0">
              <TextField
                fullWidth={true}
                floatingLabelText="Contract Duration"
                floatingLabelFixed={true}
                value={member.contractDuration}
                onChange={this.onContractDurationChange}
                disabled={
                  isDisplayMode ||
                  (cannotWrite(MEMBER, authState, adminState) && cannotUpdate(MEMBER, authState, adminState))
                }
                style={(isDisplayMode) ? fieldStyles.rootStyle : {}}
                inputStyle={(isDisplayMode) ? fieldStyles.textStyle : {}}
                floatingLabelStyle={(isDisplayMode) ? fieldStyles.textStyle : {}}
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-lg-7 col-md-12 m-auto">
            <RadioButtonGroup
              name="threeTypesOfAttendances"
              defaultSelected={this.props.selectedMemberType === CLASS_BASED ? 'remainingClasses' :
                this.props.selectedMemberType === TIME_BASED ? 'maxAttendance' :
                  'package'}
              className="row"
              onChange={this.onMembershipAttendanceTypeChange}
            >
              <RadioButton
                value="maxAttendance"
                label="Time Based"
                style={{ fontSize: 12 }}
                className="col-md-4 col-sm-12"
              />
              <RadioButton
                value="package"
                label="Package Based"
                style={{ fontSize: 12 }}
                className="col-md-4 col-sm-12"
              />
              <RadioButton
                value="remainingClasses"
                label="Class Based"
                style={{ fontSize: 12 }}
                className="col-md-4 col-sm-12"
              />
            </RadioButtonGroup>
          </div>
          <div className="col-lg-5 col-md-12">
            {this.props.selectedMemberType === CLASS_BASED ?
              <div className="row no-gutters">
                <div className="col-6">
                  <TextField
                    fullWidth={true}
                    floatingLabelText="Remaining Classes"
                    hintText="Remaining Classes"
                    disabled={true}
                    value={this.props.member.remainingClasses}
                    style={(isDisplayMode) ? fieldStyles.rootStyle : {}}
                    inputStyle={(isDisplayMode) ? fieldStyles.textStyle : {}}
                    floatingLabelStyle={(isDisplayMode) ? fieldStyles.textStyle : {}}
                  />
                </div>
                <div className="col-6 pr-4">
                  <div className="mt-2 position-absolute">
                    <IconButton
                      className="position-absolute"
                      style={{ height: '20px' }}
                      onClick={() => this.onAddMinusClassesButtonClick(true)}
                      disabled={
                        (
                          cannotWrite(MEMBER, this.props.authState, this.props.adminState) &&
                          cannotUpdate(MEMBER, this.props.authState, this.props.adminState)
                        )
                      }
                    >
                      <FontIcon className="material-icons">add_circle</FontIcon>
                    </IconButton>
                    <IconButton
                      style={{ height: '20px', top: '25px' }}
                      onClick={() => this.onAddMinusClassesButtonClick(false)}
                      disabled={
                        (
                          cannotWrite(MEMBER, this.props.authState, this.props.adminState) &&
                          cannotUpdate(MEMBER, this.props.authState, this.props.adminState)
                        )
                      }
                    >
                      <FontIcon className="material-icons">remove_circle_outline</FontIcon>
                    </IconButton>
                  </div>
                  <TextField
                    fullWidth={true}
                    floatingLabelText="Add Classes"
                    floatingLabelFixed={true}
                    value={this.state.classesToAdd}
                    onChange={this.onAddMoreClassesChange}
                    disabled={
                      (
                        this.props.selectedMemberType &&
                        this.props.selectedMemberType === TIME_BASED
                      ) ||
                      (
                        cannotWrite(MEMBER, this.props.authState, this.props.adminState) &&
                        cannotUpdate(MEMBER, this.props.authState, this.props.adminState)
                      )
                    }
                    style={fieldStyles.rootStyle}
                    inputStyle={fieldStyles.textStyle}
                    floatingLabelStyle={fieldStyles.textStyle}
                    className="ml-5"
                  />
                </div>
              </div> :
              null
            }
          </div>
        </div>
        <div className="row">
          <div className="col-lg-7 col-md-12 pr-2">
            <SelectField
              fullWidth={true}
              floatingLabelText="Max Attendance"
              value={this.state.member.maxAttendancePerWeek}
              onChange={this.onMaxAttendancePerWeekChange}
              disabled={
                isDisplayMode ||
                (
                  cannotWrite(MEMBER, this.props.authState, this.props.adminState) &&
                  cannotUpdate(MEMBER, this.props.authState, this.props.adminState)
                )
              }
              style={(isDisplayMode) ? fieldStyles.rootStyle : {}}
              labelStyle={(isDisplayMode) ? fieldStyles.textStyle : {}}
              floatingLabelStyle={(isDisplayMode) ? fieldStyles.textStyle : {}}
            >
              {
                maxAttendances.map((maxAttendance, index) => {
                  return <MenuItem key={index} value={maxAttendance.code} primaryText={maxAttendance.name} />;
                })
              }
            </SelectField>
          </div>
          <div className="col-lg-5 col-md-12">
            <div className="row no-gutters">
              <div className={(isDisplayMode) ? 'col-12' : 'col-6'}>
                {this.renderPointFields()}
              </div>
              <div className="col-6 pr-4">
                {this.renderAddOrMinusPoint(isDisplayMode)}
              </div>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-8">
            <SelectField
              floatingLabelText="Tags"
              value={this.state.tag}
              onChange={this.onTagChange}
              style={{ width: '100%' }}
            >
              {
                this.props.businessState.business && this.props.businessState.business.tags
                  .map((tag, index) => {
                    return <MenuItem key={index} value={tag} primaryText={tag} />;
                  })
              }
            </SelectField>
          </div>
          <div className="col-4">
            <RaisedButton
              primary={true}
              label="Add"
              onClick={this.addTag}
              className="mt4"
            />
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            {this.state.selectedTags.map(this.renderAddedTags)}
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <TextField
              fullWidth={true}
              floatingLabelText="Note"
              floatingLabelFixed={true}
              hintText="Note"
              multiLine={true}
              rows={4}
              value={member.note}
              onChange={this.onNoteChange}
              errorText={membersDetailsError.noteError}
              disabled={
                isDisplayMode ||
                (
                  cannotWrite(MEMBER, authState, adminState) &&
                  cannotUpdate(MEMBER, authState, adminState)
                )
              }
              style={(isDisplayMode) ? fieldStyles.rootStyle : {}}
              inputStyle={(isDisplayMode) ? fieldStyles.textStyle : {}}
              floatingLabelStyle={(isDisplayMode) ? fieldStyles.textStyle : {}}
            />
          </div>
        </div>
      </div>
    );
  }

  private renderAddedTags = (newTag: string, index: number): JSX.Element => {
    if (newTag !== '') {
      return (
        <Chip
          key={index}
          onRequestDelete={() => this.handleTagDelete(newTag)}
          style={{ margin: 4, float: 'left' }}
        >
          {newTag}
        </Chip>
      );
    } else {
      return (null);
    }
  }

  private handleTagDelete = (newTag: string): void =>
    this.setState(
      {
        selectedTags: this.state.selectedTags.filter((t: string) => newTag !== t)
      },
      () => {
        if (this.props.memberChangeHandler) {
          this.props.memberChangeHandler(
            {
              ...this.props.member,
              tags: this.state.selectedTags
            },
            false
          );
        }
      }
    )

  private onTagChange = (event: React.TouchEvent<HTMLSelectElement>, index: number, value: string): void =>
    this.setState({ tag: value })

  private addTag = (): void => this.setState(
    {
      selectedTags: [this.state.tag, ...this.state.selectedTags.filter((s: string) => s !== this.state.tag)]
    },
    () => {
      if (this.props.memberChangeHandler) {
        this.props.memberChangeHandler(
          {
            ...this.props.member,
            tags: this.state.selectedTags
          },
          false
        );
      }
    }
  )

  private renderMemberInfoFirstRow = (isDisplayMode: boolean): JSX.Element => {
    const { adminState, authState } = this.props;
    const { member, membersDetailsError } = this.state;

    return (
      <div className="row">
        <div className="col-md-6 col-sm-12">
          <div className="input-group mb-2 mr-sm-2 mb-sm-0">
            <TextField
              className="material-input-required-red"
              fullWidth={true}
              floatingLabelText="First Name"
              floatingLabelFixed={true}
              errorText={membersDetailsError.firstNameError}
              value={member.firstName}
              onChange={this.onFirstNameChange}
              disabled={
                isDisplayMode ||
                (
                  cannotWrite(MEMBER, authState, adminState) &&
                  cannotUpdate(MEMBER, authState, adminState)
                )
              }
              style={(isDisplayMode) ? fieldStyles.rootStyle : {}}
              inputStyle={(isDisplayMode) ? fieldStyles.textStyle : {}}
              floatingLabelStyle={(isDisplayMode) ? fieldStyles.textStyle : {}}
            />
          </div>
        </div>
        <div className="col-md-6 col-sm-12">
          <div className="input-group mb-2 mr-sm-2 mb-sm-0">
            <TextField
              className="material-input-required-red"
              fullWidth={true}
              floatingLabelText="Last Name"
              floatingLabelFixed={true}
              errorText={membersDetailsError.lastNameError}
              value={member.lastName}
              onChange={this.onLastNameChange}
              disabled={
                isDisplayMode ||
                (
                  cannotWrite(MEMBER, authState, adminState) &&
                  cannotUpdate(MEMBER, authState, adminState)
                )
              }
              style={(isDisplayMode) ? fieldStyles.rootStyle : {}}
              inputStyle={(isDisplayMode) ? fieldStyles.textStyle : {}}
              floatingLabelStyle={(isDisplayMode) ? fieldStyles.textStyle : {}}
            />
          </div>
        </div>
      </div>
    );
  }

  private renderMemberInfoSecondRow = (isDisplayMode: boolean): JSX.Element => {
    const { adminState, authState } = this.props;
    const { member, membersDetailsError, mobile1Error, mobile2Error } = this.state;

    const mode = isDisplayMode ||
      (
        cannotWrite(MEMBER, authState, adminState) &&
        cannotUpdate(MEMBER, authState, adminState)
      ) ? 'DISPLAY' : 'EDIT';

    return (
      <div className="row">
        <div className="col-md-12 col-sm-12">
          <div className="row">
            <div className="col-4">
              <TextField
                fullWidth={true}
                className="material-input-required-red"
                floatingLabelText="Email"
                floatingLabelFixed={true}
                errorText={membersDetailsError.emailInfoError}
                value={member.email}
                onChange={this.onEmailChange}
                disabled={
                  isDisplayMode ||
                  (
                    cannotWrite(MEMBER, authState, adminState) &&
                    cannotUpdate(MEMBER, authState, adminState)
                  )
                }
                style={(isDisplayMode) ? fieldStyles.rootStyle : {}}
                inputStyle={(isDisplayMode) ? fieldStyles.textStyle : {}}
                floatingLabelStyle={(isDisplayMode) ? fieldStyles.textStyle : {}}
              />
            </div>
            <div className="col-4">
              <TextField
                className="material-input-required-red"
                fullWidth={true}
                floatingLabelText={'Mobile1'}
                floatingLabelFixed={true}
                errorText={mobile1Error}
              >
                <Phone
                  countries={['CA']}
                  value={member.mobile1}
                  onChange={this.onMobile1Change}
                  convertToNational={true}
                  inputStyle={{ backgroundColor: '#fff', borderBottom: '0px' }}
                />
              </TextField>
            </div>
            <div className="col-4">
              <TextField
                fullWidth={true}
                floatingLabelText={'Mobile2'}
                floatingLabelFixed={true}
                errorText={mobile2Error}
              >
                <Phone
                  countries={['CA']}
                  value={member.mobile2}
                  onChange={this.onMobile2Change}
                  convertToNational={true}
                  inputStyle={{ backgroundColor: '#fff', borderBottom: '0px' }}
                />
              </TextField>
            </div>
          </div>
        </div>
        <div className="col-md-12 col-sm-12 pb-5">
          <div className="row">
            <div className="col-4">
              <DatePicker
                fullWidth={true}
                floatingLabelText="Birth Date"
                maxDate={getLocalDate(new Date())}
                value={member.birthDate ? getLocalDate(new Date(member.birthDate)) : getLocalDate(new Date())}
                onChange={this.onBirthDateChange}
                disabled={
                  isDisplayMode ||
                  (
                    cannotWrite(MEMBER, authState, adminState) &&
                    cannotUpdate(MEMBER, authState, adminState)
                  )
                }
                style={(isDisplayMode) ? fieldStyles.rootStyle : {}}
                inputStyle={(isDisplayMode) ? fieldStyles.textStyle : {}}
                floatingLabelStyle={(isDisplayMode) ? fieldStyles.textStyle : {}}
              />
            </div>
            <div className="col-4">
              <TextField
                fullWidth={true}
                floatingLabelText="Age"
                floatingLabelFixed={true}
                value={member.age}
                disabled={true}
                style={fieldStyles.rootStyle}
                inputStyle={fieldStyles.textStyle}
                floatingLabelStyle={fieldStyles.textStyle}
              />
            </div>
            <div className="col-4">
              <SelectField
                fullWidth={true}
                floatingLabelText="Gender"
                floatingLabelFixed={true}
                value={member.gender}
                onChange={this.onGenderChange}
                disabled={
                  isDisplayMode ||
                  (
                    cannotWrite(MEMBER, authState, adminState) &&
                    cannotUpdate(MEMBER, authState, adminState)
                  )
                }
                style={(isDisplayMode) ? fieldStyles.rootStyle : {}}
                labelStyle={(isDisplayMode) ? fieldStyles.textStyle : {}}
                floatingLabelStyle={(isDisplayMode) ? fieldStyles.textStyle : {}}
              >
                {
                  genders.map((gender, index) => {
                    return <MenuItem key={index} value={gender.code} primaryText={gender.name} />;
                  })
                }
              </SelectField>
            </div>
          </div>
        </div>
        <div className="col-12 pb-5">
          <AddressOnlyComponent
            addressOnlyInfo={this.state.addressOnlyInfo}
            addressOnlyInfoError={this.state.addressOnlyInfoError}
            addressOnlyInfoChangeHandler={this.addressOnlyInfoChangeHandler}
            mode={mode}
            isBusinessRegistration={false}
            businessState={this.props.businessState}
          />
        </div>
      </div>
    );
  }

  private renderMemberInfoThirdRow = (isDisplayMode: boolean): JSX.Element => {
    const { adminState, authState } = this.props;
    const { membersDetailsError, member } = this.state;

    return (
      <div className="row">
        <div className="col-lg-4 col-sm-12">
          <div className="row">
            <div className="col-12">
              Consent
            </div>
            <div className="col-4">
              <Checkbox
                label="Email"
                style={{ whiteSpace: 'nowrap' }}
                checked={member.emailConsent}
                onCheck={this.onEmailConsentChange}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  private renderMemberInfoCheckInNotificationRow = (isDisplayMode: boolean): JSX.Element => {
    const { adminState, authState } = this.props;

    return (
      <div className="row">
        <div className="col-12">
          Check In Notification
        </div>
        <div className="col-12">
          <RadioButtonGroup
            name="threeTypesOfCheckInNotification"
            defaultSelected={this.props.member.checkinNotification}
            className="row"
            onChange={this.onCheckInNotificationChange}
          >
            <RadioButton
              value="NONE"
              label="None"
              disabled={
                isDisplayMode ||
                (
                  cannotWrite(MEMBER, authState, adminState) &&
                  cannotUpdate(MEMBER, authState, adminState)
                )
              }
              style={{ fontSize: 12 }}
              className="col-2"
            />
            <RadioButton
              value="EMAIL"
              label="Email"
              disabled={
                isDisplayMode ||
                (
                  cannotWrite(MEMBER, authState, adminState) &&
                  cannotUpdate(MEMBER, authState, adminState)
                ) ||
                this.props.member.emailConsent === false
              }
              style={{ fontSize: 12 }}
              className="col-2"
            />
            <RadioButton
              value="SMS"
              label="SMS"
              disabled={
                isDisplayMode ||
                (
                  cannotWrite(MEMBER, authState, adminState) &&
                  cannotUpdate(MEMBER, authState, adminState)
                ) ||
                this.props.member.smsConsent === false
              }
              style={{ fontSize: 12 }}
              className="col-2"
            />
          </RadioButtonGroup>
        </div>
      </div>
    );
  }

  private renderPointFields = (): JSX.Element => {
    const { member } = this.state;

    return (
      <div className="input-group mb-2 mr-sm-2 mb-sm-0">
        <TextField
          fullWidth={true}
          floatingLabelText="Point"
          floatingLabelFixed={true}
          value={member.point}
          disabled={true}
          style={fieldStyles.rootStyle}
          inputStyle={fieldStyles.textStyle}
          floatingLabelStyle={fieldStyles.textStyle}
        />
      </div>
    );
  }

  private renderAddOrMinusPoint = (isDisplayMode: boolean): JSX.Element => {
    const { pointToUpdate } = this.state;
    const { authState, adminState } = this.props;

    if (isDisplayMode) {
      return (<div />);
    } else {
      return (
        <div>
          <div className="mt-2 position-absolute">
            <IconButton
              className="position-absolute"
              style={{ height: '20px' }}
              onClick={() => this.onAddMinusPointButtonClick(true)}
              disabled={
                (
                  cannotWrite(MEMBER, authState, adminState) &&
                  cannotUpdate(MEMBER, authState, adminState)
                )
              }
            >
              <FontIcon className="material-icons">add_circle</FontIcon>
            </IconButton>
            <IconButton
              style={{ height: '20px', top: '25px' }}
              onClick={() => this.onAddMinusPointButtonClick(false)}
              disabled={
                (
                  cannotWrite(MEMBER, authState, adminState) &&
                  cannotUpdate(MEMBER, authState, adminState)
                )
              }
            >
              <FontIcon className="material-icons">remove_circle_outline</FontIcon>
            </IconButton>
          </div>
          <TextField
            fullWidth={true}
            floatingLabelText=" "
            floatingLabelFixed={true}
            value={pointToUpdate}
            onChange={this.onPointToUpdateChange}
            disabled={
              (
                cannotWrite(MEMBER, authState, adminState) &&
                cannotUpdate(MEMBER, authState, adminState)
              )
            }
            className="ml-5"
          />
        </div>
      );
    }
  }

  private toggleWarningDialog = (message: string, callback?: Function): void =>
    this.setState({
      warningDialogOpen: !this.state.warningDialogOpen,
      warningDialogMessage: message,
      warningDialogCallback: callback
    })

  private toggleMemershipStatus = (): void =>
    this.setState(
      { warningDialogOpen: false },
      () => {
        const { memberChangeHandler } = this.props;

        if (memberChangeHandler) {
          return memberChangeHandler(
            { ...this.props.member, cancelled: !this.props.member.cancelled },
            this.hasMemberDetailInfoError()
          );
        }
      }
    )

  private onFirstNameChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void =>
    this.setState(
      {
        member: {
          ...this.state.member,
          firstName: newValue
        },
        membersDetailsError: {
          ...this.state.membersDetailsError,
          firstNameError: !isValidName(newValue) ?
            generateEnterValidErrorMessage('first name') :
            newValue.length > MAX_LENGTH_FOR_DB ?
              'Name is too long' :
              ''
        }
      },
      () => {
        const { memberChangeHandler } = this.props;
        if (memberChangeHandler) {
          memberChangeHandler(
            { ...this.props.member, firstName: newValue },
            this.hasMemberDetailInfoError()
          );
        }
      }
    )

  private onLastNameChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void => {
    const { memberChangeHandler } = this.props;

    this.setState(
      {
        member: {
          ...this.state.member,
          lastName: newValue
        },
        membersDetailsError: {
          ...this.state.membersDetailsError,
          lastNameError: !isValidName(newValue) ?
            generateEnterValidErrorMessage('last name') :
            newValue.length > MAX_LENGTH_FOR_DB ?
              'Name is too long' :
              ''
        }
      },
      () => {
        if (memberChangeHandler) {
          memberChangeHandler(
            { ...this.props.member, lastName: newValue },
            this.hasMemberDetailInfoError()
          );
        }
      }
    );
  }

  private onInitialChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void =>
    this.setState(
      {
        member: {
          ...this.state.member,
          initial: newValue
        },
        membersDetailsError: {
          ...this.state.membersDetailsError,
          initialError: newValue.length > 20 ?
            'Initial is too long' :
            validator.isAlpha(newValue) || newValue === '' ? '' : 'Please enter a valid initial.'
        }
      },
      () => {
        const { memberChangeHandler } = this.props;
        if (memberChangeHandler) {
          memberChangeHandler(
            { ...this.props.member, initial: newValue },
            this.hasMemberDetailInfoError()
          );
        }
      }
    )

  private onGenderChange = (event: React.TouchEvent<HTMLSelectElement>, index: number, value: GenderCode): void => {
    const { memberChangeHandler } = this.props;
    if (memberChangeHandler) {
      memberChangeHandler(
        {
          ...this.props.member,
          gender: value
        },
        this.hasMemberDetailInfoError()
      );
    }
  }

  private onBirthDateChange = (event: React.FormEvent<HTMLInputElement>, date: Date): void => {
    const { memberChangeHandler } = this.props;
    if (memberChangeHandler) {
      memberChangeHandler(
        {
          ...this.props.member,
          birthDate: getLocalDate(date).getTime(),
          age: calculateAge(getLocalDate(date))
        },
        this.hasMemberDetailInfoError()
      );
    }
  }

  private onAddMinusPointButtonClick = (isAdd: Boolean): void => {
    const { pointToUpdate } = this.state;
    const { member, memberChangeHandler } = this.props;
    if (memberChangeHandler) {
      memberChangeHandler(
        {
          ...member,
          point: isAdd ?
            member.point + pointToUpdate :
            member.point - pointToUpdate > 0 ? member.point - pointToUpdate : 0
        },
        this.hasMemberDetailInfoError()
      );
    }
  }

  private onAddMinusClassesButtonClick = (isAdd: Boolean): void => {
    const { classesToAdd } = this.state;
    const { member, memberChangeHandler } = this.props;

    if (memberChangeHandler) {
      memberChangeHandler(
        {
          ...member,
          remainingClasses: isAdd ?
            member.remainingClasses + classesToAdd :
            member.remainingClasses > 0 && member.remainingClasses - classesToAdd >= 0 ?
              member.remainingClasses - classesToAdd :
              0
        },
        this.hasMemberDetailInfoError()
      );
    }
  }

  private onMaxAttendancePerWeekChange = (
    event: React.TouchEvent<HTMLSelectElement>, index: number, value: MaxAttendanceCode
  ): void => {
    const { memberChangeHandler } = this.props;

    if (memberChangeHandler) {
      memberChangeHandler(
        { ...this.props.member, maxAttendancePerWeek: value },
        this.hasMemberDetailInfoError()
      );
    }
  }

  private onPointToUpdateChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void => {
    if (validator.isNumeric(newValue)) {
      const newPointToUpdateIntVal = parseInt(newValue, 10);

      this.setState({
        pointToUpdate: isNaN(newPointToUpdateIntVal) ? 0 : newPointToUpdateIntVal
      });
    }
  }

  private onEmailChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void =>
    this.setState(
      {
        member: {
          ...this.state.member,
          email: newValue
        },
        membersDetailsError: {
          ...this.state.membersDetailsError,
          emailInfoError: isValidEmail(newValue, 'Please enter a valid email')
        }
      },
      () => {
        const { memberChangeHandler } = this.props;
        if (memberChangeHandler) {
          memberChangeHandler(
            { ...this.props.member, email: newValue },
            this.hasMemberDetailInfoError()
          );
        }
      }
    )

  private addressOnlyInfoChangeHandler = (newAddressOnlyInfo: AddressOnlyInfo,
    newAddressOnlyInfoError: AddressOnlyInfoError): void =>
    this.setState(
      {
        addressOnlyInfo: newAddressOnlyInfo,
        addressOnlyInfoError: newAddressOnlyInfoError
      },
      () => {
        const { memberChangeHandler } = this.props;

        if (memberChangeHandler) {
          memberChangeHandler(
            {
              ...this.props.member,
              address: newAddressOnlyInfo.address,
              city: newAddressOnlyInfo.city,
              country: newAddressOnlyInfo.country,
              province: newAddressOnlyInfo.province,
              postalCode: newAddressOnlyInfo.postalCode
            },
            this.hasMemberDetailInfoError()
          );
        }
      }
    )

  private onCheckInNotificationChange = (event: React.FormEvent<{}>, selected: string): void => {
    this.setState(
      {
        member: {
          ...this.state.member,
          checkinNotification: selected
        }
      },
      () => {
        const { memberChangeHandler } = this.props;
        if (memberChangeHandler) {
          memberChangeHandler(
            { ...this.props.member, checkinNotification: selected },
            this.hasMemberDetailInfoError()
          );
        }
      }
    );
  }

  private onNoteChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void => {
    this.setState(
      {
        membersDetailsError: {
          ...this.state.membersDetailsError,
          noteError: newValue.length > MAX_NOTE_LENGTH ? 'Note is too long' : ''
        }
      },
      () => {
        const { memberChangeHandler } = this.props;

        if (memberChangeHandler) {
          memberChangeHandler(
            { ...this.props.member, note: newValue },
            this.hasMemberDetailInfoError()
          );
        }
      }
    );
  }

  private onMembershipStartDateChange = (event: React.FormEvent<HTMLInputElement>, date: Date): void => {
    const { memberChangeHandler } = this.props;
    const endDate = (this.props.selectedMemberType === TIME_BASED) ?
      getLocalDate(new Date(date.getFullYear() + 1, date.getMonth(), date.getDate())).getTime() :
      getLocalDate(date).getTime();
    if (memberChangeHandler) {
      this.setState(
        {
          membersDetailsError: {
            ...this.state.membersDetailsError,
            startEndDateError: compareNumber(getLocalDate(date).getTime(), endDate) < 0 ?
              '' :
              generateEnterValidErrorMessage('end date')
          }
        },
        () => {
          if (memberChangeHandler) {
            memberChangeHandler(
              {
                ...this.props.member,
                startDate: getLocalDate(date).getTime(),
                endDate: endDate
              },
              this.hasMemberDetailInfoError()
            );
          }
        }
      );
    }
  }

  private onMembershipEndDateChange = (event: React.FormEvent<HTMLInputElement>, date: Date): void => {
    const newEndDate = getLocalDate(date).getTime();
    const { memberChangeHandler } = this.props;

    this.setState(
      {
        membersDetailsError: {
          ...this.state.membersDetailsError,
          startEndDateError: compareNumber(this.props.member.startDate, newEndDate) < 0 ?
            '' :
            generateEnterValidErrorMessage('end date')
        }
      },
      () => {
        if (memberChangeHandler) {
          memberChangeHandler(
            {
              ...this.props.member,
              endDate: newEndDate
            },
            this.hasMemberDetailInfoError()
          );
        }
      }
    );
  }

  private onMembershipAttendanceTypeChange = (event: React.FormEvent<{}>, selected: string): void => {
    const {
      selectedMemberTypeChangeHandler,
      memberChangeHandler
    } = this.props;

    const attendanceType = selected === 'maxAttendance' ? TIME_BASED :
      selected === 'remainingClasses' ? CLASS_BASED : PACKAGE_BASED;

    if (selectedMemberTypeChangeHandler) {
      selectedMemberTypeChangeHandler(attendanceType);
    }

    const today = getLocalDate(new Date()).getTime();
    if (memberChangeHandler && attendanceType === CLASS_BASED) {
      this.setState(
        {
          membersDetailsError: {
            ...this.state.membersDetailsError,
            startEndDateError: compareNumber(today, today) < 0 ?
              '' :
              generateEnterValidErrorMessage('end date')
          }
        },
        () => {
          if (memberChangeHandler) {
            memberChangeHandler(
              {
                ...this.props.member,
                startDate: today,
                endDate: today
              },
              this.hasMemberDetailInfoError()
            );
          }
        }
      );
    } else if (memberChangeHandler && attendanceType === PACKAGE_BASED) {
      this.setState(
        {
          membersDetailsError: {
            ...this.state.membersDetailsError,
            startEndDateError: compareNumber(this.props.member.startDate, YEAR_3000_PACKAGE_BASED_END_DATE) < 0 ?
              '' :
              generateEnterValidErrorMessage('end date')
          }
        },
        () => {
          if (memberChangeHandler) {
            memberChangeHandler(
              {
                ...this.props.member,
                startDate: today,
                endDate: YEAR_3000_PACKAGE_BASED_END_DATE
              },
              this.hasMemberDetailInfoError()
            );
          }
        }
      );
    } else {
      const newDate = new Date();
      this.setState(
        {
          membersDetailsError: {
            ...this.state.membersDetailsError,
            startEndDateError: compareNumber(
              this.props.member.startDate,
              new Date(newDate.getFullYear() + 1, newDate.getMonth(), newDate.getDate()).getTime()
            ) < 0 ?
              '' :
              generateEnterValidErrorMessage('end date')
          }
        },
        () => {
          if (memberChangeHandler) {
            memberChangeHandler(
              {
                ...this.props.member,
                startDate: today,
                endDate: new Date(newDate.getFullYear() + 1, newDate.getMonth(), newDate.getDate()).getTime()
              },
              this.hasMemberDetailInfoError()
            );
          }
        }
      );
    }
  }

  private onContractDurationChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void => {
    const newContractDuration = event.currentTarget.value;
    const { member, memberChangeHandler } = this.props;

    if (memberChangeHandler) {
      memberChangeHandler(
        {
          ...member,
          contractDuration: newContractDuration
        },
        this.hasMemberDetailInfoError()
      );
    }
  }

  private onAddMoreClassesChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void => {
    if (validator.isNumeric(newValue)) {
      const newClassesToAdd = parseInt(newValue, 10);

      this.setState({
        classesToAdd: isNaN(newClassesToAdd) ? 0 : newClassesToAdd
      });
    }
  }

  private onSmsConsentChange(event: React.MouseEvent<HTMLInputElement>, isInputChecked: boolean) {
    const { member, memberChangeHandler } = this.props;

    if (memberChangeHandler) {
      memberChangeHandler(
        {
          ...member,
          smsConsent: isInputChecked,
          checkinNotification: (member.checkinNotification === 'SMS') ? 'NONE' : member.checkinNotification
        },
        this.hasMemberDetailInfoError()
      );
    }
  }

  private onEmailConsentChange(event: React.MouseEvent<HTMLInputElement>, isInputChecked: boolean) {
    const { member, memberChangeHandler } = this.props;

    if (memberChangeHandler) {
      memberChangeHandler(
        {
          ...member,
          emailConsent: isInputChecked
        },
        this.hasMemberDetailInfoError()
      );
    }
  }

  private onProgramMembersChangeHandler() {
    const { member, memberChangeHandler } = this.props;

    if (memberChangeHandler) {
      memberChangeHandler(
        member,
        this.hasMemberDetailInfoError()
      );
    }
  }

  private onEmergencyContactName1Change = (event: React.FormEvent<HTMLInputElement>, newValue: string): void => {
    const { memberChangeHandler } = this.props;

    this.setState(
      {
        member: {
          ...this.state.member,
          emergencyContactName1: newValue
        },
        membersDetailsError: {
          ...this.state.membersDetailsError,
          emergencyContactName1Error: !isValidName(newValue) ?
            generateEnterValidErrorMessage('name') :
            newValue.length > MAX_LENGTH_FOR_DB ?
              'Name is too long' :
              ''
        }
      },
      () => {
        if (memberChangeHandler) {
          memberChangeHandler(
            { ...this.props.member, emergencyContactName1: newValue },
            this.hasMemberDetailInfoError()
          );
        }
      }
    );
  }
  private onEmergencyContactName2Change = (event: React.FormEvent<HTMLInputElement>, newValue: string): void => {
    const { memberChangeHandler } = this.props;

    this.setState(
      {
        member: {
          ...this.state.member,
          emergencyContactName2: newValue
        },
        membersDetailsError: {
          ...this.state.membersDetailsError,
          emergencyContactName2Error: !isValidName(newValue) ?
            generateEnterValidErrorMessage('name') :
            newValue.length > MAX_LENGTH_FOR_DB ?
              'Name is too long' :
              ''
        }
      },
      () => {
        if (memberChangeHandler) {
          memberChangeHandler(
            { ...this.props.member, emergencyContactName2: newValue },
            this.hasMemberDetailInfoError()
          );
        }
      }
    );
  }
  private onEmergencyContactName3Change = (event: React.FormEvent<HTMLInputElement>, newValue: string): void => {
    const { memberChangeHandler } = this.props;

    this.setState(
      {
        member: {
          ...this.state.member,
          emergencyContactName3: newValue
        },
        membersDetailsError: {
          ...this.state.membersDetailsError,
          emergencyContactName3Error: !isValidName(newValue) ?
            generateEnterValidErrorMessage('name') :
            newValue.length > MAX_LENGTH_FOR_DB ?
              'Name is too long' :
              ''
        }
      },
      () => {
        if (memberChangeHandler) {
          memberChangeHandler(
            { ...this.props.member, emergencyContactName3: newValue },
            this.hasMemberDetailInfoError()
          );
        }
      }
    );
  }

  private onEmergencyContactNumber1Change = (newPhone: string): void => {
    this.setState(
      {
        membersDetailsError: {
          ...this.state.membersDetailsError,
          emergencyContactNumber1Error: isValidPhone(newPhone, 'Please enter a valid phone number')
        }
      },
      () => {
        const { member, memberChangeHandler } = this.props;
        if (memberChangeHandler) {
          memberChangeHandler(
            {
              ...member,
              emergencyContactNumber1: newPhone
            },
            this.hasMemberDetailInfoError()
          );
        }
      }
    );
  }

  private onEmergencyContactNumber2Change = (newPhone: string): void => {
    this.setState(
      {
        membersDetailsError: {
          ...this.state.membersDetailsError,
          emergencyContactNumber2Error: isValidPhone(newPhone, 'Please enter a valid phone number')
        }
      },
      () => {
        const { member, memberChangeHandler } = this.props;
        if (memberChangeHandler) {
          memberChangeHandler(
            {
              ...member,
              emergencyContactNumber2: newPhone
            },
            this.hasMemberDetailInfoError()
          );
        }
      }
    );
  }

  private onEmergencyContactNumber3Change = (newPhone: string): void => {
    this.setState(
      {
        membersDetailsError: {
          ...this.state.membersDetailsError,
          emergencyContactNumber3Error: isValidPhone(newPhone, 'Please enter a valid phone number')
        }
      },
      () => {
        const { member, memberChangeHandler } = this.props;
        if (memberChangeHandler) {
          memberChangeHandler(
            {
              ...member,
              emergencyContactNumber3: newPhone
            },
            this.hasMemberDetailInfoError()
          );
        }
      }
    );
  }

  private hasMemberDetailInfoError = (): boolean => {
    const { membersDetailsError, addressOnlyInfoError } = this.state;
    const { paymentError } = this.state;

    return membersDetailsError.amountPerTransactionError !== ''
      || membersDetailsError.downPaymentError !== ''
      || membersDetailsError.firstNameError !== ''
      || membersDetailsError.lastNameError !== ''
      || membersDetailsError.initialError !== ''
      || membersDetailsError.startEndDateError !== ''
      || membersDetailsError.emailInfoError !== ''
      || paymentError.numOfTransactionError !== ''
      || membersDetailsError.noteError !== ''
      || membersDetailsError.emergencyContactName1Error !== ''
      || membersDetailsError.emergencyContactName2Error !== ''
      || membersDetailsError.emergencyContactName3Error !== ''
      || membersDetailsError.emergencyContactNumber1Error !== ''
      || membersDetailsError.emergencyContactNumber2Error !== ''
      || membersDetailsError.emergencyContactNumber3Error !== ''
      || addressOnlyInfoError.addressError !== ''
      || addressOnlyInfoError.cityError !== ''
      || addressOnlyInfoError.provinceError !== ''
      || addressOnlyInfoError.countryError !== ''
      || addressOnlyInfoError.postalCodeError !== '';
  }

  private renderCancelButton = (onClickFunction: (e: React.SyntheticEvent<{}>) => void): JSX.Element => {
    return (
      <FlatButton
        label="Cancel"
        primary={true}
        onClick={onClickFunction}
      />
    );
  }

  private renderSubmitButton = (onClickFunction: (e: React.SyntheticEvent<{}>) => void): JSX.Element => {
    return (
      <FlatButton
        label="Submit"
        primary={true}
        keyboardFocused={true}
        onClick={onClickFunction}
      />
    );
  }

  private getFirstPaymentFromMember = (props: Props): PaymentInfo => props.member.payments[0] ?
    props.member.payments[0] :
    DEFAULT_PAYMENT

  private renderAmountEditable = (cellInfo) => {
    return (
      <div>
        <input
          style={{
            backgroundColor: '#fafafa'
          }}
          contentEditable={true}
          suppressContentEditableWarning={true}
          onChange={(e) => this.handleScheduledAmountChange(e, cellInfo)}
          value={this.props.member.scheduledPayments ?
            this.props.member.scheduledPayments[cellInfo.index].amount :
            null
          }
          disabled={this.getFirstPaymentFromMember(this.props).paymentMethod.paymentMethodType !== 'VOIDCHEQUE'}
        />
        {
          this.props.member.scheduledPayments[cellInfo.index].editableFieldError !== '' ?
            <div className="col-12 red">
              {this.props.member.scheduledPayments[cellInfo.index].editableFieldError}
            </div> :
            null
        }
      </div>
    );
  }

  private renderChargeDateEditable = (cellInfo) => {
    return (
      <ReactDatePicker
        selected={this.props.member.scheduledPayments ?
          this.props.member.scheduledPayments[cellInfo.index].chargeDate :
          null
        }
        onChange={(date) => this.handleChargeDateChange(date, cellInfo)}
        disabled={this.getFirstPaymentFromMember(this.props).paymentMethod.paymentMethodType !== 'VOIDCHEQUE'}
      />
    );
  }

  private handleChargeDateChange = (date, cellInfo) => {
    const { member, memberChangeHandler } = this.props;

    if (memberChangeHandler) {
      memberChangeHandler(
        {
          ...member,
          scheduledPayments: member.scheduledPayments ?
            [
              ...member.scheduledPayments.slice(0, cellInfo.index),
              {
                ...member.scheduledPayments[cellInfo.index],
                chargeDate: date,
                status: member.scheduledPayments[cellInfo.index].scheduledPaymentId ?
                  this.isScheduleChanged(cellInfo.index) ||
                    date !== this.state.originalMember.scheduledPayments[cellInfo.index].chargeDate ?
                    UPDATED :
                    REMAINED :
                  null
              },
              ...member.scheduledPayments.slice(cellInfo.index + 1)
            ] :
            []
        },
        this.hasMemberDetailInfoError()
      );
    }
  }

  private handleScheduledAmountChange = (e, cellInfo) => {
    const { member, memberChangeHandler } = this.props;
    const value = e.target.value;

    if (memberChangeHandler) {
      memberChangeHandler(
        {
          ...member,
          scheduledPayments: member.scheduledPayments ?
            [
              ...member.scheduledPayments.slice(0, cellInfo.index),
              {
                ...member.scheduledPayments[cellInfo.index],
                amount: value,
                editableFieldError: value !== '' &&
                  validator.isDecimal(value) &&
                  (value.includes('.') ? value.split('.')[1].length <= 2 : true) ?
                  '' :
                  'Please enter valid value.',
                status: member.scheduledPayments[cellInfo.index].scheduledPaymentId ?
                  this.isScheduleChanged(cellInfo.index) ||
                    value !== this.state.originalMember.scheduledPayments[cellInfo.index].amount ?
                    UPDATED :
                    REMAINED :
                  null
              },
              ...member.scheduledPayments.slice(cellInfo.index + 1)
            ] :
            []
        },
        this.hasMemberDetailInfoError()
      );
    }
  }

  private isScheduleChanged = (i: number): boolean => {
    const { member, originalMember } = this.state;

    return member.scheduledPayments[i].amount !== originalMember.scheduledPayments[i].amount ||
      member.scheduledPayments[i].chargeDate !== originalMember.scheduledPayments[i].chargeDate;
  }

  private onMobile1Change = (newPhone: string): void =>
    this.setState(
      {
        member: {
          ...this.state.member,
          mobile1: newPhone
        },
        mobile1Error: isValidPhone(newPhone, 'Please enter a valid mobile2 number')
      },
      () => {
        const { memberChangeHandler } = this.props;
        if (memberChangeHandler) {
          memberChangeHandler(
            { ...this.props.member, mobile1: newPhone },
            this.hasMemberDetailInfoError()
          );
        }
      }
    )

  private onMobile2Change = (newPhone: string): void =>
    this.setState(
      {
        member: {
          ...this.state.member,
          mobile2: newPhone
        },
        mobile2Error: isValidPhone(newPhone, 'Please enter a valid mobile2 number')
      },
      () => {
        const { memberChangeHandler } = this.props;
        if (memberChangeHandler) {
          memberChangeHandler(
            { ...this.props.member, mobile2: newPhone },
            this.hasMemberDetailInfoError()
          );
        }
      }
    )
}

export default MembersDetailsComponent;



// WEBPACK FOOTER //
// ./src/members/members.details.component.tsx